<template>
  <q-layout view="lHh Lpr lFf">
    <q-page-container>
      <q-page class="auth-page flex flex-center">
        <div class="auth-container">
          <!-- Brand -->
          <div class="auth-brand">
            <div class="brand-logo">
              <img src="https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/icon2.png" alt="Logo" />
            </div>
            <div class="brand-name">GoldenKey</div>
            <div class="brand-sub">Portal Imobiliário</div>
          </div>

          <!-- Card -->
          <div class="auth-card">
            <div class="card-top">
              <div class="card-title">
                {{ isReset ? 'Redefinir Senha' : 'Alterar Senha' }}
              </div>
              <div class="card-sub">
                {{ isReset ? 'Crie uma nova senha para a sua conta' : 'Atualize a sua senha de acesso' }}
              </div>
            </div>

            <q-form @submit="handleSubmit" class="auth-form">
              <!-- Senha Actual (só aparece se não for reset) -->
              <div v-if="!isReset" class="field-wrap">
                <label class="field-label">Senha Actual</label>
                <q-input
                  v-model="form.currentPassword"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  outlined
                  placeholder="••••••••"
                  class="auth-input"
                  :rules="[val => !!val || 'Senha actual é obrigatória']"
                  lazy-rules
                  :error="!!errors.currentPassword"
                  :error-message="errors.currentPassword"
                >
                  <template v-slot:prepend>
                    <q-icon name="lock" size="18px" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      :name="showCurrentPassword ? 'visibility_off' : 'visibility'"
                      size="18px"
                      class="cursor-pointer"
                      @click="showCurrentPassword = !showCurrentPassword"
                    />
                  </template>
                </q-input>
              </div>

              <!-- Nova Senha -->
              <div class="field-wrap">
                <label class="field-label">Nova Senha</label>
                <q-input
                  v-model="form.newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  outlined
                  placeholder="••••••••"
                  class="auth-input"
                  :rules="[
                    val => !!val || 'Nova senha é obrigatória',
                    val => val.length >= 6 || 'Mínimo 6 caracteres',
                    val => !form.currentPassword || val !== form.currentPassword || 'Deve ser diferente da senha actual'
                  ]"
                  lazy-rules
                  :error="!!errors.newPassword"
                  :error-message="errors.newPassword"
                >
                  <template v-slot:prepend>
                    <q-icon name="lock_outline" size="18px" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      :name="showNewPassword ? 'visibility_off' : 'visibility'"
                      size="18px"
                      class="cursor-pointer"
                      @click="showNewPassword = !showNewPassword"
                    />
                  </template>
                </q-input>
              </div>

              <!-- Confirmar Nova Senha -->
              <div class="field-wrap">
                <label class="field-label">Confirmar Nova Senha</label>
                <q-input
                  v-model="form.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  outlined
                  placeholder="••••••••"
                  class="auth-input"
                  :rules="[
                    val => !!val || 'Confirmação é obrigatória',
                    val => val === form.newPassword || 'As senhas não coincidem'
                  ]"
                  lazy-rules
                  :error="!!errors.confirmPassword"
                  :error-message="errors.confirmPassword"
                >
                  <template v-slot:prepend>
                    <q-icon name="verified_user" size="18px" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      :name="showConfirmPassword ? 'visibility_off' : 'visibility'"
                      size="18px"
                      class="cursor-pointer"
                      @click="showConfirmPassword = !showConfirmPassword"
                    />
                  </template>
                </q-input>
              </div>

              <!-- Requisitos -->
              <div class="req-banner">
                <q-icon name="info_outline" size="16px" class="req-icon" />
                <span>Mínimo 6 caracteres · combine letras e números para maior segurança</span>
              </div>

              <!-- Submit -->
              <button type="submit" class="btn-submit" :disabled="loading">
                <q-spinner v-if="loading" size="16px" color="white" class="q-mr-xs" />
                <q-icon v-else name="check_circle" size="18px" class="q-mr-xs" />
                {{ isReset ? 'Redefinir Senha' : 'Alterar Senha' }}
              </button>
            </q-form>
          </div>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import router from '@/router'
import store from '@/vuex/store'
import Swal from 'sweetalert2'

const route = useRoute()

const form = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const errors = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)
const loading = ref(false)
const resetToken = ref(null)

const isReset = computed(() => !!resetToken.value)

onMounted(() => {
  resetToken.value = route.query.token || route.query.reset || null
})

const handleSubmit = async () => {
  loading.value = true
  errors.value = { currentPassword: '', newPassword: '', confirmPassword: '' }

  try {
    if (isReset.value) {
      await store.dispatch('auth/changePassword', {
        password: form.value.newPassword,
        password_confirmation: form.value.confirmPassword,
        forced: 1
      })
      Swal.fire({
        icon: 'success',
        title: 'Senha Redefinida',
        text: 'A sua senha foi redefinida com sucesso!',
        confirmButtonText: 'OK'
      }).then(() => { router.push('/profile') })
    } else {
      await store.dispatch('auth/changePassword', {
        password_actual: form.value.currentPassword,
        password: form.value.newPassword,
        password_confirmation: form.value.confirmPassword,
        forced: 0
      })
      Swal.fire({
        icon: 'success',
        title: 'Senha Alterada',
        text: 'A sua senha foi alterada com sucesso!',
        confirmButtonText: 'OK'
      }).then(() => { router.push('/profile') })
    }
  } catch (error) {
    console.error('Erro ao processar senha:', error)
    let errorMessage = 'Ocorreu um erro ao processar a solicitação.'
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (!isReset.value) {
      errorMessage = 'Senha actual incorrecta. Por favor, tente novamente.'
    } else {
      errorMessage = 'Token inválido ou expirado. Solicite um novo link de recuperação.'
    }
    Swal.fire({ icon: 'error', title: 'Erro', text: errorMessage })
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
/* ── Page ── */
.auth-page {
  min-height: 100vh;
  background: #0d1b2a;
}

/* ── Container ── */
.auth-container {
  width: 100%;
  max-width: 420px;
  padding: 24px 16px;
}

/* ── Brand ── */
.auth-brand {
  text-align: center;
  margin-bottom: 28px;
}
.brand-logo {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  border: 2px solid rgba(212, 175, 55, 0.35);
  overflow: hidden;
  margin: 0 auto 12px;
  background: rgba(255, 255, 255, 0.04);
}
.brand-logo img { width: 100%; height: 100%; object-fit: cover; }
.brand-name {
  font-size: 22px;
  font-weight: 800;
  color: #d4af37;
  letter-spacing: 0.5px;
  margin-bottom: 3px;
}
.brand-sub {
  font-size: 11px;
  color: rgba(212, 175, 55, 0.5);
  text-transform: uppercase;
  letter-spacing: 1.2px;
  font-weight: 600;
}

/* ── Card ── */
.auth-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 32px 28px 28px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.35);
}
.card-top { margin-bottom: 24px; }
.card-title {
  font-size: 20px;
  font-weight: 700;
  color: #0d1b2a;
  margin-bottom: 4px;
}
.card-sub { font-size: 13px; color: #6b7280; }

/* ── Form ── */
.auth-form { display: flex; flex-direction: column; gap: 4px; }
.field-wrap { margin-bottom: 4px; }
.field-label {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  letter-spacing: 0.2px;
}

:deep(.auth-input .q-field__control) {
  border-radius: 12px !important;
  background: #fafbfc !important;
}
:deep(.auth-input.q-field--focused .q-field__control) {
  border-color: #d4af37 !important;
  box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.12);
}

/* ── Req Banner ── */
.req-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: rgba(212, 175, 55, 0.07);
  border: 1px solid rgba(212, 175, 55, 0.22);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 12px;
  color: #7a6420;
  line-height: 1.5;
  margin-top: 4px;
}
.req-icon { color: #d4af37; flex-shrink: 0; margin-top: 1px; }

/* ── Submit ── */
.btn-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 16px;
  padding: 13px 20px;
  border-radius: 12px;
  border: none;
  background: #0d1b2a;
  color: #d4af37;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}
.btn-submit:hover { background: #162940; }
.btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

@media (max-width: 480px) {
  .auth-card { padding: 24px 18px 20px; }
}
</style>
